import React, { PropsWithChildren, ReactElement } from "react";
import { createMemoryHistory } from "history";
import { QueryClient, QueryClientProvider } from "react-query";
import { Route, Router } from "react-router-dom";
import { act, render, RenderResult } from "@testing-library/react";

import { FindingFiltersProvider } from "@providers/FindingFiltersProvider";
import { OrgContext } from "@providers/OrgProvider";
import { PermissionsContext } from "@providers/PermissionsProvider";
import { TimeLookbackProvider } from "@providers/TimeLookbackProvider";
import { UserContext } from "@providers/UserProvider";
import { OrgData, Permission, UserData } from "@types";

/** Renders a React element, then waits for the result of a testing-library "find" query */
export const renderAndFind = async <E extends ReactElement>(
  element: E,
  test: (renderResult: RenderResult) => Promise<any>
): Promise<HTMLElement> => {
  let match: HTMLElement;
  await act(async () => {
    const result = render(element);
    await test(result);
    match = result.container;
  });
  return match!;
};

export const renderAndFindBaseElement = async <E extends ReactElement>(
  element: E,
  test: (renderResult: RenderResult) => Promise<any>
): Promise<Element> => {
  let match: Element;
  await act(async () => {
    const result = render(element);
    await test(result);
    match = result.baseElement;
  });
  return match!;
};

/** Wrapper for testing components that require a router */
export const RenderWithRouter: React.FC<
  React.PropsWithChildren<{
    route: string;
    path: string;
    state?: any;
  }>
> = (props) => {
  const history = createMemoryHistory({ initialEntries: [props.path] });
  if (props.state) {
    history.push({ state: props.state });
  }

  return (
    <Router history={history}>
      <Route path={props.route}>{props.children}</Route>
    </Router>
  );
};

/** Basic user and org for contexts */
export const testOrg: OrgData = {
  name: "TestOrg",
  slug: "testorg",
  id: "1",
  has_rbac: false,
  has_autofix: false,
};

const testUser: UserData = {
  username: "testuser",
  auth_provider_type: "github",
  orgs: [testOrg],
};

/** Wrapper for testing components that require org and user contexts */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // https://react-query.tanstack.com/guides/testing#turn-off-retries
      retry: false,
      // https://react-query.tanstack.com/guides/testing#set-cachetime-to-infinity-with-jest
      cacheTime: Infinity,
    },
  },
});

export const RenderWithContexts: React.FC<
  PropsWithChildren<{
    userData?: UserData | null;
    orgData?: OrgData | null;
    permissions?: Permission[];
  }>
> = ({
  userData = testUser,
  orgData = testOrg,
  permissions = [],
  children,
}) => {
  return (
    <QueryClientProvider client={queryClient}>
      <UserContext.Provider
        value={{ user: userData !== null ? userData : undefined }}
      >
        <OrgContext.Provider
          value={{
            org: orgData !== null ? orgData : undefined,
            setOrg: () => {},
          }}
        >
          <PermissionsContext.Provider value={{ permissions }}>
            <TimeLookbackProvider>
              <FindingFiltersProvider>{children}</FindingFiltersProvider>
            </TimeLookbackProvider>
          </PermissionsContext.Provider>
        </OrgContext.Provider>
      </UserContext.Provider>
    </QueryClientProvider>
  );
};
